import { useEffect, useRef, useState } from 'react';
import type { Teksten } from '../teksten';

/**
 * Wat de app verstond, vóór het vertaald wordt.
 *
 * Het idee van Giovanni: je leest je eigen tekst en ziet zelf of er iets fout
 * verstaan is. Anders vertaalt de app netjes de verkeerde zin en snapt de
 * ander er niets van - zonder dat iemand weet waar het misging.
 *
 * DE AFTELLING IS HET HELE PUNT. Een venster waar je op OK moet klikken, maakt
 * van elk gesprek een klikoefening. Een venster dat vanzelf doorgaat, kost
 * niets zolang alles goed gaat en geeft je een paar seconden als het misgaat.
 *
 * Zodra je het tekstvak aanraakt, stopt de teller. Dan ben je aan het
 * verbeteren en hoort de app te wachten - ook als dat een minuut duurt.
 *
 * EN ER IS EEN STAND ZONDER TELLER. Wie een zin altijd wil nakijken, wil niet
 * elke keer het vak aanraken om de klok stil te zetten - dan is de teller geen
 * hulp maar iets om tegen te vechten. Met "altijd zelf versturen" staat er
 * geen balk en gebeurt er niets tot je op de knop drukt.
 *
 * De balk loopt VLOEIEND leeg en niet in stapjes van een seconde. Een cijfer
 * dat van 3 naar 2 springt zegt je niet hoeveel tijd je nog hebt; een balk die
 * halverwege staat wel.
 */

/** Zo vaak de balk bijwerken. 50 ms is vloeiend genoeg en kost niets. */
const TIK_MS = 50;

export default function Bevestiging({
  tekst,
  seconden,
  handmatig,
  t,
  opVerstuur,
  opAnnuleer,
}: {
  tekst: string;
  /** Hoeveel tijd je krijgt om te verbeteren. Instelbaar met de schuif. */
  seconden: number;
  /** Geen teller: het bericht wacht tot je zelf op versturen drukt. */
  handmatig: boolean;
  t: Teksten;
  opVerstuur: (tekst: string) => void;
  opAnnuleer: () => void;
}) {
  const [waarde, setWaarde] = useState(tekst);
  const [over, setOver] = useState(seconden * 1000);
  const [telt, setTelt] = useState(!handmatig && seconden > 0);
  const veldRef = useRef<HTMLTextAreaElement>(null);

  /**
   * Het vak groeit met de tekst mee.
   *
   * Bij twee vaste regels moest je in een venstertje van drie regels scrollen
   * om je eigen zin na te kijken - precies het tegenovergestelde van waar dit
   * venster voor is. Nu tot ongeveer tien regels, daarna scrollt het pas.
   */
  useEffect(() => {
    const veld = veldRef.current;
    if (veld === null) return;
    veld.style.height = 'auto';
    veld.style.height = `${Math.min(veld.scrollHeight, 280)}px`;
  }, [waarde]);

  useEffect(() => {
    if (!telt) return;
    if (over <= 0) {
      opVerstuur(waarde.trim());
      return;
    }
    const timer = window.setTimeout(() => setOver((n) => n - TIK_MS), TIK_MS);
    return () => window.clearTimeout(timer);
  }, [telt, over, waarde, opVerstuur]);

  function stopTeller(): void {
    setTelt(false);
  }

  const deel = !handmatig && seconden > 0 ? Math.max(0, over) / (seconden * 1000) : 0;

  return (
    <div className="border-2 border-gio-orange/40 bg-gio-orange/5 rounded-2xl p-3 animate-fade-in">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gio-orange-deep">
          {t.verstaanAls}
        </p>
        {!telt && (
          <p className="text-[10px] text-slate-450">
            {handmatig ? t.jijVerstuurtZelf : t.tellerStaatStil}
          </p>
        )}
      </div>

      {/* De balk loopt leeg. Stopt de teller, dan blijft hij staan waar hij is.
          In de handmatige stand is er niets om te tonen: er loopt geen klok. */}
      <div
        className={`h-1 rounded-full bg-gio-orange/15 overflow-hidden mb-2 ${
          handmatig ? 'invisible' : ''
        }`}
      >
        <div
          className={`h-full rounded-full ${telt ? 'bg-gio-orange' : 'bg-slate-350'}`}
          style={{
            width: `${deel * 100}%`,
            transition: telt ? `width ${TIK_MS}ms linear` : 'none',
          }}
        />
      </div>

      <textarea
        ref={veldRef}
        value={waarde}
        onChange={(e) => {
          stopTeller();
          setWaarde(e.target.value);
        }}
        onFocus={stopTeller}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            opVerstuur(waarde.trim());
          }
          if (e.key === 'Escape') opAnnuleer();
        }}
        rows={3}
        className="w-full p-3 bg-white border-2 border-slate-250 rounded-xl text-lg
                   text-slate-800 leading-snug resize-none overflow-y-auto
                   focus:outline-none focus:border-gio-orange transition-colors duration-155"
      />

      <div className="flex items-center gap-2 mt-2">
        <button
          type="button"
          onClick={() => opVerstuur(waarde.trim())}
          disabled={waarde.trim() === ''}
          className="flex-1 py-2 rounded-xl bg-gio-orange hover:bg-gio-orange-deep text-white
                     font-bold text-xs transition-colors duration-155 disabled:opacity-40"
        >
          {t.versturen}
        </button>
        <button
          type="button"
          onClick={opAnnuleer}
          className="px-3 py-2 rounded-xl border-2 border-slate-250 text-xs font-bold
                     text-slate-550 hover:text-gio-danger hover:border-gio-danger/40
                     transition-colors duration-155"
        >
          {t.wis}
        </button>
      </div>
    </div>
  );
}
